import { useState, ChangeEvent, RefObject, useEffect } from 'react'
import { Combobox } from '@headlessui/react'
import { CheckIcon } from '@heroicons/react/20/solid'
import type { AutoCompleteOption } from '../../shared/interfaces'


type AutocompleteProps = {
  options: AutoCompleteOption[],
  className: string,
  props?: any,
  setSelected?: (value: AutoCompleteOption | null) => void | undefined,
  selected: AutoCompleteOption | null,
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void,
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void,
  dropdownAbove?: boolean,
  inputRef?: RefObject<HTMLInputElement | null>,
}

const Autocomplete: React.FC<AutocompleteProps> = ({
  options,
  className,
  selected,
  setSelected,
  dropdownAbove = false,
  inputRef,
  props: passthroughProps,
  onKeyDown,
  onChange,
  ...rest
}) => {
  const [query, setQuery] = useState('')
  const [pendingEnterMove, setPendingEnterMove] = useState(false)

  // Sync visible input text when parent sets selection (e.g. edit user with saved party)
  useEffect(() => {
    if (selected) {
      setQuery(selected.displayName || selected.name || selected.code || String(selected.id))
    } else {
      setQuery('')
    }
  }, [selected])

  const filteredOption =
    query === ''
      ? options
      : options.filter((option) => {
          const searchText = query.toLowerCase();
          const nameMatch = option.name?.toLowerCase().includes(searchText);
          const codeMatch = option.code?.toLowerCase().includes(searchText);
          const displayNameMatch = option.displayName?.toLowerCase().includes(searchText);
          return nameMatch || codeMatch || displayNameMatch;
        })

  const inputProps = { ...rest, ...(passthroughProps || {}) };
  const parentOnKeyDown =
    (inputProps.onKeyDown as (e: React.KeyboardEvent<HTMLInputElement>) => void | undefined) ??
    onKeyDown;
  const parentOnChange =
    (inputProps.onChange as (e: ChangeEvent<HTMLInputElement>) => void | undefined) ?? onChange;

  // Handle selection change - if there was a pending Enter move, move to next field
  const handleSelectionChange = (value: AutoCompleteOption | null) => {
    if (setSelected) {
      setSelected(value)
      setQuery('')
      
      // If Enter was pressed and we just selected a value, move to next field
      if (pendingEnterMove && value) {
        setPendingEnterMove(false)
        // Create a synthetic event for the parent handler with proper form reference
        const currentInput = inputRef?.current;
        // Only proceed if input exists and has a form
        if (currentInput && currentInput.form && typeof parentOnKeyDown === 'function') {
          const syntheticEvent = {
            key: 'Enter',
            preventDefault: () => {},
            stopPropagation: () => {},
            currentTarget: currentInput,
          } as React.KeyboardEvent<HTMLInputElement>
          
          setTimeout(() => {
            parentOnKeyDown(syntheticEvent);
          }, 100);
        }
      }
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    let handled = false
    if (e.key === 'Enter') {
      // Prevent default form submission
      e.preventDefault();
      e.stopPropagation();
      
      const text = (e.currentTarget as HTMLInputElement).value || ''
      const searchText = text.toLowerCase().trim();
      
      // Only select if there's text entered and we can find an exact match
      if (searchText && filteredOption.length > 0 && setSelected) {
        // Set flag to move to next field after selection
        setPendingEnterMove(true)
        
        // Try to find exact match only
        const exact = filteredOption.find(o => 
          o.code?.toLowerCase() === searchText || 
          o.name?.toLowerCase() === searchText ||
          o.displayName?.toLowerCase() === searchText
        )
        
        // Only select if exact match found, otherwise leave empty and move to next field
        if (exact) {
          handleSelectionChange(exact)
          handled = true
          return;
        }
        // If no exact match, clear the pending move flag and continue to move to next field
        setPendingEnterMove(false)
      } else {
        // If no text entered, clear selection
        setPendingEnterMove(false)
        if (setSelected && !searchText) {
          // Clear selection if input is empty
          setSelected(null)
          setQuery('')
        }
      }
      
      // Always move to next field when Enter is pressed (whether match found or not)
      // Only call parent handler if currentTarget exists and has form property
      if (typeof parentOnKeyDown === 'function' && e.currentTarget && e.currentTarget.form) {
        setTimeout(() => {
          parentOnKeyDown(e);
        }, 50);
      }
      handled = true;
    } else if (e.key === 'Tab' && !e.shiftKey) {
      // When Tab is pressed, try to select a party if text is entered
      if (setSelected && !selected) {
        const text = (e.currentTarget as HTMLInputElement).value || ''
        if (text.trim()) {
          const searchText = text.toLowerCase();
          const localFiltered = text
            ? options.filter(o => {
                const nameMatch = o.name?.toLowerCase().includes(searchText);
                const codeMatch = o.code?.toLowerCase().includes(searchText);
                const displayNameMatch = o.displayName?.toLowerCase().includes(searchText);
                return nameMatch || codeMatch || displayNameMatch;
              })
            : options
          const exact = localFiltered.find(o => 
            o.code?.toLowerCase() === searchText || 
            o.name?.toLowerCase() === searchText ||
            o.displayName?.toLowerCase() === searchText
          )
          const pick = exact || localFiltered[0]
          if (pick) {
            e.preventDefault()
            e.stopPropagation()
            setSelected(pick)
            setQuery('')
            handled = true
            // After selecting, call parent handler to move to next field
            setTimeout(() => {
              if (typeof parentOnKeyDown === 'function' && e.currentTarget && e.currentTarget.form) {
                parentOnKeyDown(e)
              }
            }, 0)
            return
          }
        }
      }
      // If a party is already selected, let Tab propagate to parent handler
      if (selected && typeof parentOnKeyDown === 'function' && e.currentTarget && e.currentTarget.form) {
        parentOnKeyDown(e)
        return
      }
    }
    // Call parent handler for other keys, but only if form exists
    if (typeof parentOnKeyDown === 'function' && !handled && e.currentTarget && e.currentTarget.form) {
      parentOnKeyDown(e)
    }
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value)
    if (typeof parentOnChange === 'function') parentOnChange(event)
  }

  const optionsPlacement = dropdownAbove ? 'bottom-full mb-1' : 'mt-1'

  const { className: propsClassName, placeholder, ...restProps } = inputProps as Record<string, unknown>;
  const inputClassName = className || (propsClassName as string) || '';
  const inputPlaceholder = (placeholder as string) || 'Search...';
  
  return (
    <div className="w-full">
      <Combobox
        value={selected}
        onChange={handleSelectionChange}
        by={(a: AutoCompleteOption | null, b: AutoCompleteOption | null) =>
          a != null && b != null && String(a.id) === String(b.id)
        }
      >
        <div className="relative">
          <Combobox.Input
            ref={inputRef}
            displayValue={(option: AutoCompleteOption | null) =>
              option?.displayName || option?.name || option?.code || ''
            }
            placeholder={inputPlaceholder}
            className={`w-full h-[30px] px-2 py-1 border border-gray-300 bg-white text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${inputClassName}`}
            {...(restProps as object)}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
          />
          {filteredOption.length > 0 && (
            <Combobox.Options className={`absolute z-50 ${optionsPlacement} max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg border border-gray-300 focus:outline-none sm:text-sm`}>
              {filteredOption.map((option) => (
                <Combobox.Option
                  key={option.id}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 pl-3 pr-4 ${active ? 'bg-blue-600 text-white' : 'text-gray-900'}`
                  }
                  value={option}
                >
                  {({ selected, active }) => (
                    <>
                      <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                        {option.displayName || option.name}
                      </span>
                      {selected ? (
                        <span className={`absolute inset-y-0 left-0 flex items-center pl-1 ${active ? 'text-white' : 'text-blue-600'}`}>
                          <CheckIcon className="h-4 w-4" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </Combobox.Option>
              ))}
            </Combobox.Options>
          )}
        </div>
      </Combobox>
    </div>
  )
}

export default Autocomplete
